import React from 'react';
import { preloadImg } from 'helpers';
import { NodeGroup } from 'react-move';
import { easeQuadOut } from 'd3-ease';
import cx from 'classnames';
import T from 'prop-types';
import styles from './cardStack.css';
import MobileFrameIcon from './MobileFrameIcon';

const initializeDataArray = (dataArray) => {
  const result = [...dataArray, ...dataArray]
    .map((item, index) => ({ ...item, _index: `${index}` }))
    .reverse();
  return result;
};

class CardStack extends React.Component {
  state = {
    deltaX: 0,
    mouseX: 0,
    isPressed: false,
    cards: initializeDataArray(this.props.dataArray),
    index: 0,
  };

  componentDidMount() {
    this.props.dataArray.forEach(preloadImg);
  }

  handleTouchStart = (pressLocation, e) => {
    this.handleMouseDown(pressLocation, e.touches[0]);
  };

  handleMouseDown = (pressX, { pageX }) => {
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('touchend', this.handleMouseUp);
    window.addEventListener('touchmove', this.handleTouchMove);
    this.setState({
      deltaX: pageX - pressX,
      mouseX: pressX,
      isPressed: true,
    });
  };

  handleTouchMove = (e) => {
    this.handleMouseMove(e.touches[0]);
  };

  handleMouseMove = ({ pageX }) => {
    const { isPressed, deltaX } = this.state;

    if (isPressed) {
      const mouseX = pageX - deltaX;
      this.setState({ mouseX });
    }
  };

  handleMouseUp = () => {
    this.setState({ isPressed: false, deltaX: 0 });
    if (
      this.state.mouseX > this.xThreshold.max ||
      this.state.mouseX < this.xThreshold.min
    ) {
      this.hideCard();
    }
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('touchend', this.handleMouseUp);
    window.removeEventListener('touchmove', this.handleTouchMove);
  };

  xThreshold = {
    min: this.props.xCenter - this.props.xAmplitude,
    max: this.props.xCenter + this.props.xAmplitude,
  }; // min and max thresholds for first card movement, before it dissapears

  hideCard = () => {
    const newCardsArray = [
      ...this.state.cards.slice(-1),
      ...this.state.cards.slice(0, -1),
    ];

    this.setState(
      {
        cards: newCardsArray,
        index: +newCardsArray[0]._index,
      },
      () => this.props.indexCb(this.state.index),
    );
  };

  hideCardLeft = false;

  hideCardOnClick = () => {
    this.hideCardLeft = true;
    this.hideCard();
  };

  render() {
    const {
      Card,
      className,
      overlayColor,
      xOffsetsArray,
      yOffsetsArray,
      scalesArray,
      leaveTargetArray,
      cardsQuantity,
      opacityArray,
      mode,
    } = this.props;

    const { cards, isPressed, mouseX } = this.state;

    return (
      <NodeGroup
        data={cards.slice(-cardsQuantity)}
        keyAccessor={d => d._index}
        start={(data, index) => ({
          x: xOffsetsArray[index],
          y: yOffsetsArray[index],
          s: scalesArray[index],
          o: opacityArray[index],
        })}
        enter={(data, index) => ({
          x: [xOffsetsArray[index]],
          y: [yOffsetsArray[index]],
          s: [scalesArray[index]],
          o: opacityArray[index],

          timing: { duration: 400, ease: easeQuadOut },
        })}
        leave={(data, index) => {
          Promise.resolve().then(() => {
            this.hideCardLeft = false;
          });
          return {
            x: this.hideCardLeft
              ? [leaveTargetArray[0]]
              : mouseX > this.xThreshold.min
                ? [leaveTargetArray[1]]
                : [leaveTargetArray[0]],
            y: [yOffsetsArray[index]],
            s: scalesArray[index],

            timing: { duration: 500, ease: easeQuadOut },
          };
        }}
        update={(data, index) => {
          const xOffset =
            isPressed && index === cardsQuantity - 1
              ? mouseX
              : xOffsetsArray[index];
          return {
            x: [xOffset],
            y: [yOffsetsArray[index]],
            s: [scalesArray[index]],
            o: [opacityArray[index]],

            timing: { duration: 400, ease: easeQuadOut },
          };
        }}
      >
        {interpolated => (
          <div className={cx(styles.root, styles[mode], className)}>
            {interpolated.slice(0, -1).map(({ key, state, data }) => (
              <div
                role="presentation"
                className={styles.card}
                key={key}
                style={{
                  transform: `translate3d(${state.x}%, ${state.y}%, 0) scale(${state.s})`,
                }}
              >
                <div
                  style={{
                    backgroundColor: `${overlayColor}`,
                    opacity: `${state.o}`,
                    visibility: 'visible',
                  }}
                  className={styles.overlay}
                />
                <Card {...data} />
              </div>
            ))}
            {interpolated.slice(-1).map(({ key, state, data }) => (
              <div
                role="presentation"
                className={styles.card}
                key={key}
                onClick={this.hideCardOnClick}
                onMouseDown={this.handleMouseDown.bind(null, state.x)}
                onTouchStart={this.handleTouchStart.bind(null, state.x)}
                style={{
                  transform: `translate3d(${state.x}%, ${state.y}%, 0) scale(${state.s})`,
                  zIndex: '5',
                }}
              >
                <div
                  style={{
                    backgroundColor: `${overlayColor}`,
                    opacity: `${state.o}`,
                  }}
                  className={styles.overlay}
                />
                <Card {...data} />
              </div>
            ))}
            {mode === 'sliderDesktop' && (
              <MobileFrameIcon className={styles.mobileFrameIcon} />
            )}
          </div>
        )}
      </NodeGroup>
    );
  }
}

CardStack.defaultProps = {
  className: '',
  cardsQuantity: 3,
  yOffsetsArray: [-56, -53, -50],
  xOffsetsArray: [-44, -47, -50],
  yCenter: -50,
  xCenter: -50,
  xAmplitude: 30,
  leaveTargetArray: [-200, 100],
  overlayColor: null,
  scalesArray: [1, 1, 1],
  zIndexArray: [2, 1, 0],
  opacityArray: [0.7, 0.7, 0],
  indexCb: () => {},
};

CardStack.propTypes = {
  Card: T.oneOfType([T.element, T.func]).isRequired,
  className: T.string,
  dataArray: T.array.isRequired,
  indexCb: T.func,
  leaveTargetArray: T.arrayOf(T.number),
  overlayColor: T.string,
  cardsQuantity: T.number, // visible quantity of cards in stack
  scalesArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  yOffsetsArray: T.arrayOf(T.number), // offsets in percents for 2nd and etc cards
  xOffsetsArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  yCenter: T.number, // transform x coordinate of first card
  xCenter: T.number, // transform y coordinate of first card
  xAmplitude: T.number, // amplitude of first card movement

  zIndexArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  opacityArray: T.arrayOf(T.number), // scales for 2nd and etc cards
};

export default CardStack;



// WEBPACK FOOTER //
// ./src/components/modules/CardStack/CardStack.js